import { makeAutoObservable } from "mobx"
import type { TranslationKeys } from "../i18n/translations"

export type Locale = "en" | "de" | "es" | "zh"
export type TranslationKey = keyof TranslationKeys

const LOCALE_STORAGE_KEY = "devpulse-locale"

export class I18nStore {
  locale: Locale = "en"
  private translations: Record<Locale, TranslationKeys>

  constructor(translations: Record<Locale, TranslationKeys>) {
    this.translations = translations
    makeAutoObservable(this)
    this.loadLocale()
  }

  private loadLocale() {
    try {
      const stored = localStorage.getItem(LOCALE_STORAGE_KEY)
      if (stored && (stored === "en" || stored === "de" || stored === "es" || stored === "zh")) {
        this.locale = stored
      }
    } catch (error) {
      console.warn("Failed to load locale from localStorage:", error)
    }
  }

  setLocale(locale: Locale) {
    this.locale = locale
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale)
    } catch (error) {
      console.warn("Failed to save locale to localStorage:", error)
    }
  }

  t(key: TranslationKey): string {
    return this.translations[this.locale][key] || key
  }

  // Helper for dynamic translations with parameters
  tf(key: TranslationKey, params: Record<string, string | number>): string {
    let text = this.t(key)
    Object.entries(params).forEach(([paramKey, value]) => {
      text = text.replace(`{${paramKey}}`, String(value))
    })
    return text
  }
}

export const createI18nStore = (translations: Record<Locale, TranslationKeys>) => {
  return new I18nStore(translations)
}
